import React from "react";
import { MdRefresh } from "react-icons/md";
import Card from "components/card";
import { UserFinancialInfo as FinancialInfoType } from "../../types/financial";
import SubscriptionOverviewCard from "./SubscriptionOverviewCard";
import TransactionHistoryCard from "./TransactionHistoryCard";
import LoadingSpinner from "../common/LoadingSpinner";

interface UserFinancialInfoProps {
  financialInfo: FinancialInfoType | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

const UserFinancialInfo: React.FC<UserFinancialInfoProps> = ({
  financialInfo,
  loading,
  error,
  onRefresh,
}) => {
  if (loading) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <LoadingSpinner />
      </Card>
    );
  }

  if (error) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <div className="py-12 text-center">
          <div className="mb-4 text-lg text-red-500">{error}</div>
          <button
            onClick={onRefresh}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <MdRefresh className="mr-2 h-4 w-4" />
            Retry
          </button>
        </div>
      </Card>
    );
  }

  if (!financialInfo) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Subscription Overview - ALWAYS show to display subscription status and totals */}
      <SubscriptionOverviewCard
        hasActiveSubscription={financialInfo.has_active_subscription}
        stripeData={financialInfo.stripe_data}
        revenueCatSubscriptions={financialInfo.revenue_cat_subscriptions}
        revenueCatTransactions={financialInfo.revenue_cat_transactions}
      />

      {/* Unified Transaction History - Stripe purchases and every RevenueCat transaction */}
      <TransactionHistoryCard
        stripePurchases={financialInfo.stripe_data?.purchases || []}
        revenueCatSubscriptions={financialInfo.revenue_cat_subscriptions || []}
        revenueCatTransactions={
          financialInfo.revenue_cat_transactions?.transactions || []
        }
        revenueCatHistoryAvailable={
          financialInfo.revenue_cat_transactions?.available ?? true
        }
        revenueCatHistoryHasMore={
          financialInfo.revenue_cat_transactions?.has_more ?? false
        }
        upcomingPlan={
          financialInfo.subscription_overview?.upcoming_plan ?? null
        }
        onRetryRevenueCatHistory={onRefresh}
      />
    </div>
  );
};

export default UserFinancialInfo;
